/**
 * Payment Utilities
 * Centralized functions for mapping payment methods and statuses to display-friendly labels
 */

// Payment method mapping from database values to display names
export const mapPaymentMethodToDisplay = (dbValue) => {
  if (!dbValue) return 'N/A';
  
  const methodMap = {
    'cod': 'Cash on Delivery',
    'cashfree': 'Cashfree Payment',
    'razorpay': 'Razorpay Payment',
    'phonepe': 'PhonePe Payment',
    'offline': 'Offline Payment',
    'bank_transfer': 'Bank Transfer',
    'credit_card': 'Credit Card',
    'debit_card': 'Debit Card',
    'upi': 'UPI',
    'paypal': 'PayPal',
    'stripe': 'Stripe',
    'paytm': 'Paytm',
    'googlepay': 'Google Pay',
    'amazonpay': 'Amazon Pay'
  };
  
  return methodMap[dbValue.toLowerCase()] || dbValue;
};

// Payment status mapping from database values to display names
export const mapPaymentStatusToDisplay = (dbValue) => {
  if (!dbValue) return 'Unknown';
  
  const statusMap = {
    'pending': 'Pending',
    'completed': 'Completed',
    'failed': 'Failed',
    'cancelled': 'Cancelled',
    'processing': 'Processing',
    'refunded': 'Refunded',
    'confirmed': 'Confirmed'
  };
  
  return statusMap[dbValue.toLowerCase()] || dbValue;
};

// Order status mapping from database values to display names
export const mapOrderStatusToDisplay = (dbValue) => {
  if (!dbValue) return 'Unknown';
  
  const statusMap = {
    'pending': 'Pending',
    'confirmed': 'Confirmed',
    'processing': 'Processing',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'returned': 'Returned'
  };
  
  return statusMap[dbValue.toLowerCase()] || dbValue;
};

// Get status badge component with proper styling
export const getStatusBadge = (status, type = 'payment') => {
  const statusConfig = {
    payment: {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: 'Clock' },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: 'CheckCircle' },
      failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: 'XCircle' },
      cancelled: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200', icon: 'XCircle' },
      processing: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: 'AlertCircle' },
      refunded: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', icon: 'AlertCircle' },
      confirmed: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: 'CheckCircle' }
    },
    order: {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: 'Clock' },
      confirmed: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: 'CheckCircle' },
      processing: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: 'AlertCircle' },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: 'CheckCircle' },
      cancelled: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: 'XCircle' },
      shipped: { color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200', icon: 'Package' },
      delivered: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: 'CheckCircle' },
      returned: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', icon: 'RotateCcw' }
    }
  };

  const config = statusConfig[type]?.[status.toLowerCase()] || statusConfig[type]?.pending || statusConfig.payment.pending;
  
  return {
    className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`,
    displayText: mapPaymentStatusToDisplay(status),
    icon: config.icon
  };
};

// Get status color for text styling
export const getStatusColor = (status, type = 'payment') => {
  const config = {
    payment: {
      completed: 'text-green-600 dark:text-green-400',
      confirmed: 'text-green-600 dark:text-green-400',
      pending: 'text-yellow-600 dark:text-yellow-400',
      failed: 'text-red-600 dark:text-red-400',
      cancelled: 'text-red-600 dark:text-red-400',
      processing: 'text-blue-600 dark:text-blue-400',
      refunded: 'text-orange-600 dark:text-orange-400'
    },
    order: {
      completed: 'text-green-600 dark:text-green-400',
      confirmed: 'text-blue-600 dark:text-blue-400',
      pending: 'text-yellow-600 dark:text-yellow-400',
      processing: 'text-purple-600 dark:text-purple-400',
      cancelled: 'text-red-600 dark:text-red-400',
      shipped: 'text-indigo-600 dark:text-indigo-400',
      delivered: 'text-green-600 dark:text-green-400',
      returned: 'text-orange-600 dark:text-orange-400'
    }
  };

  return config[type]?.[status.toLowerCase()] || config[type]?.pending || config.payment.pending;
};

// Get status icon component
export const getStatusIcon = (status, type = 'payment') => {
  const iconMap = {
    Clock: 'Clock',
    CheckCircle: 'CheckCircle',
    XCircle: 'XCircle',
    AlertCircle: 'AlertCircle',
    Package: 'Package',
    RotateCcw: 'RotateCcw'
  };

  const statusConfig = {
    payment: {
      pending: 'Clock',
      completed: 'CheckCircle',
      confirmed: 'CheckCircle',
      failed: 'XCircle',
      cancelled: 'XCircle',
      processing: 'AlertCircle',
      refunded: 'AlertCircle'
    },
    order: {
      pending: 'Clock',
      confirmed: 'CheckCircle',
      processing: 'AlertCircle',
      completed: 'CheckCircle',
      cancelled: 'XCircle',
      shipped: 'Package',
      delivered: 'CheckCircle',
      returned: 'RotateCcw'
    }
  };

  const iconName = statusConfig[type]?.[status.toLowerCase()] || statusConfig[type]?.pending || 'Clock';
  return iconMap[iconName] || 'Clock';
};
